import React from 'react';
import { Button, useToasts } from 'freemium-ui';
import { isNil } from 'lodash';
import moment from 'moment-timezone';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useHistory } from 'react-router-dom';
import { DEFAULT_LANGUAGE_OPTIONS } from 'routes/screens/response/constants';
import {
  getOrderedPageData,
  getRatingIcon,
} from 'routes/screens/response/utils';
import Icon from 'shared/components/Icon';
import Spacer from 'shared/components/Spacer';
import {
  AccountInfoType,
  RangeLabelType,
  SurveyBlockWithQuestionType,
  SurveyQuestionType,
  SurveyResponseType,
  SurveyType,
} from 'shared/types';
import { getAccountTimeFormat, getChannelName } from 'shared/utils';
import { useAccessControl } from 'utils/access-control';
import {
  useResponseFavouriteMutation,
  useSaveResponseTranslation,
} from '../../../hooks';
import {
  getIconGlyph,
  getResponseStatus,
  getStatusIconGlyph,
} from '../../list/utils';
import { ValueBox } from '../../list/style';
import { Invalid, RCardExtended, ResponseCardContent, Tag } from '../style';
import ResponseTags from './ResponseTags';
import ResponseCardBlockHolder from './response-card-block-holder';

type ResponseCardType = {
  rating: number;
  ratingType: RangeLabelType | 'loading';
  ratingTypeId: string;
  surveyBlocks: SurveyBlockWithQuestionType[];
  surveyQuestions: SurveyQuestionType[];
  contactName: string;
  isLoadingContact: boolean;
  surveyInfo: SurveyType;
  accountInfo?: AccountInfoType;
  responseData: SurveyResponseType;
  placeholders: Record<string, string>;
};

const ResponseCard = (props: ResponseCardType) => {
  const { t } = useTranslation();

  const {
    placeholders,
    responseData,
    surveyInfo,
    ratingType,
    rating,
    surveyQuestions,
    accountInfo,
    surveyBlocks,
    contactName,
    isLoadingContact,
    ratingTypeId,
  } = props;
  const form = useForm({});
  const { handleSubmit } = form;

  const { is_invalid, is_edited, is_non_actionable, is_favourite } =
    responseData;
  const [, setToast] = useToasts();
  const { mutate: markFavourite, isLoading: isFavouriteLoading } =
    useResponseFavouriteMutation(setToast);
  const history = useHistory();
  const { access } = useAccessControl();

  const timestamp = moment(responseData?.created_at)
    .tz(accountInfo?.timezone || moment.tz.guess())
    .format(getAccountTimeFormat(accountInfo));

  const toggleFavourite = () => {
    if (surveyInfo?.id)
      markFavourite({
        surveyId: surveyInfo?.id,
        responseId: responseData?.surveyserv_response_id,
        fav: !!is_favourite,
        response_display_id: responseData?.id,
      });
  };

  const defaultQuestion = surveyQuestions.find(
    (que) => !!que.type_info?.pivot_question,
  );
  const range = defaultQuestion?.type_info?.scale_properties?.range;
  const isBooleanQuestionType =
    defaultQuestion?.type_info?.question_type === 'BOOLEAN' ||
    (range?.length === 2 && range?.[0].min === 0 && range?.[1].min === 1);
  const targetLanguage = accountInfo?.locale || 'en';
  const language = DEFAULT_LANGUAGE_OPTIONS.map((l) => ({
    ...l,
    name: t(l.name),
  })).find((lang) => targetLanguage === lang.id);
  const responseLanguage = DEFAULT_LANGUAGE_OPTIONS.map((l) => ({
    ...l,
    name: t(l.name),
  })).find((lang) => lang.id === responseData?.language);
  const overAllScore = responseData?.detailed_response_score;

  const [isResponseTranslated, setIsResponseTranslated] = React.useState(false);
  const [responseTranslation, setResponseTranslation] = React.useState<any>();
  const customerSentiment =
    responseData?.freddy_response_analysis?.customer_sentiment;

  const {
    mutate: saveResponseTranslation,
    isLoading: isLoadingResponseTranslation,
  } = useSaveResponseTranslation({
    setToast,
    callback: (isSuccess, data) => {
      if (isSuccess) {
        setResponseTranslation(data);
        setIsResponseTranslated(true);
      }
    },
  });
  const onSubmit = () => {
    saveResponseTranslation({
      survey_id: responseData?.survey_id || '',
      response_id: responseData?.surveyserv_response_id || '',
      targetLanguage: targetLanguage,
      sourceLanguage: responseData?.language || '',
    });
  };

  const pageData = isResponseTranslated
    ? responseTranslation?.page_data
    : responseData?.page_data;

  const page_data = getOrderedPageData(pageData, surveyInfo);

  return (
    <RCardExtended className="response-card" isPages={page_data?.length}>
      {is_invalid || is_edited || is_non_actionable ? (
        <div className="row left-align">
          {is_invalid ? (
            <Invalid> {t('responses.card.marked_invalid')} </Invalid>
          ) : null}
          {is_edited ? (
            <Tag isLabel> {t('responses.card.response_edited')}</Tag>
          ) : null}
          {is_non_actionable ? (
            <Tag isLabel>
              {t('responses.filter_condition.response_fields.non_actionable')}
            </Tag>
          ) : null}
        </div>
      ) : null}
      <div className="row">
        <div className="user-info">
          {surveyInfo.type !== 'NONE' && !isNil(rating) && (
            <ValueBox
              style={{ marginRight: '12px' }}
              label={ratingType}
              className={`rating extended ${ratingType}`}
            >
              {isBooleanQuestionType &&
              defaultQuestion?.type_info?.type_variant &&
              ratingType !== 'loading' ? (
                <Icon
                  glyph={getRatingIcon(
                    defaultQuestion?.type_info?.type_variant,
                    ratingType as RangeLabelType,
                  )}
                  fill={
                    defaultQuestion?.type_info?.type_variant === 'EMOJI'
                      ? 'none'
                      : ''
                  }
                  width={18}
                  viewBox="0 0 16 16"
                  height={18}
                />
              ) : (
                rating
              )}
            </ValueBox>
          )}
          <div>
            {contactName && !isLoadingContact ? (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events
              <h5
                // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
                role="button"
                tabIndex={0}
                className={`name ${contactName.replace(' ', '')}`}
                title={contactName}
                onClick={() => {
                  if (
                    contactName !== 'Deleted contact' &&
                    contactName !== 'Anonymous user'
                  )
                    history.push(
                      `/contacts/${responseData?.ucr_contact_id}?tab=recentResponses`,
                    );
                }}
              >
                {contactName}
              </h5>
            ) : (
              <Skeleton width={150} height={25} />
            )}
            <div
              className="channel"
              style={{ color: 'var(--secondary-color)' }}
            >
              <Icon
                glyph={getIconGlyph(responseData?.channel || 'EMAIL')}
                width={14}
                height={14}
                fill="#12344d"
              />
              <Spacer x={0.25} />
              {responseData.channel && getChannelName(responseData.channel)
                ? t(getChannelName(responseData.channel))
                : `${t('email_cp')}`}
              <Spacer x={1} />
              <div
                className="channel"
                style={{ color: 'var(--secondary-color)' }}
              >
                <Icon
                  glyph={getStatusIconGlyph(responseData?.status || 'COMPLETE')}
                  width={14}
                  height={14}
                  fill="#12344d"
                />
                <Spacer x={0.25} />
                {getResponseStatus(responseData?.status || 'COMPLETE')}
              </div>
            </div>
          </div>
        </div>
        <div className="time-rating">
          {customerSentiment && (
            <div className="sentiment-button">
              <Icon glyph="copilot" width="14" height="14" />{' '}
              {t('responses.freddy_sentiment.sentiment')}:{' '}
              {t(
                `responses.freddy_sentiment.${customerSentiment.toLowerCase()}`,
              )}
            </div>
          )}
          {accountInfo?.account_meta?.response_translation_enabled &&
            responseData.language !== targetLanguage && (
              <div className="translation">
                {!isResponseTranslated ? (
                  <Button
                    onClick={(event: React.MouseEvent) => {
                      event.preventDefault();
                      handleSubmit(onSubmit, (err) =>
                        console.log('ERR', err),
                      )();
                    }}
                    disabled={isLoadingResponseTranslation}
                    className="translation-to-other-language"
                  >
                    <Icon
                      glyph="translation"
                      width="12"
                      height="16"
                      fill="#2C5CC5"
                    />
                    {`${t('responses.response_translation.translate_to')} ${
                      language?.name
                    }`}
                  </Button>
                ) : (
                  <div className="show-original-container">
                    <div className="translate-language">
                      <Icon
                        glyph="translation"
                        width="12"
                        height="16"
                        fill="#12344D"
                      />
                      {language?.name}{' '}
                      <Icon
                        glyph="pointer-right"
                        width="8"
                        height="6"
                        viewBox="0 0 8 6"
                        fill="none"
                      />{' '}
                      {responseLanguage?.name}
                    </div>
                    <div>
                      <Button
                        onClick={() => setIsResponseTranslated(false)}
                        className="show-original"
                      >
                        {t('responses.response_translation.show_original')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

          <div className="timestamp">{timestamp}</div>
          {!isFavouriteLoading ? (
            <Icon
              glyph={is_favourite ? 'filled_favourite' : 'favourite'}
              width={18}
              height={18}
              onClick={() =>
                access?.includes('response:write') && toggleFavourite()
              }
            />
          ) : (
            <Skeleton width={22} circle height={22} />
          )}
        </div>
      </div>
      <ResponseCardContent
        // style={{ width: '75%' }}
        className={`content ${is_invalid ? 'invalid-content' : ''}`}
      >
        <ResponseCardBlockHolder
          survey={surveyInfo}
          response={responseData}
          surveyBlocks={surveyBlocks}
          ratingTypeId={ratingTypeId}
          accountInfo={accountInfo}
          placeholders={placeholders}
          isResponseTranslated={isResponseTranslated}
          responseTranslation={responseTranslation}
          isLoadingResponseTranslation={isLoadingResponseTranslation}
        />
        {!isNil(overAllScore) && (
          <div className="overall-score">
            <label>{t('responses.card.overall_score')}</label>
            <div className="score-chip">
              <span className="given-score">
                {overAllScore?.cumulative_given_score}
              </span>
              <span className="max-score">
                {overAllScore?.cumulative_max_score}
              </span>
            </div>
          </div>
        )}
      </ResponseCardContent>
      <div
        className={
          access?.includes('response-edit:write')
            ? 'card-footer'
            : 'card-footer-readonly card-footer'
        }
      >
        <div className="tag-google-container">
          <ResponseTags
            surveyId={surveyInfo?.id}
            responseData={responseData}
            isResponseTranslated={isResponseTranslated}
          />
          {accountInfo?.account_meta?.response_translation_enabled &&
            isResponseTranslated && (
              <div className="translate-container">
                <Trans
                  i18nKey="responses.google_translate"
                  components={{
                    google: (
                      <Icon
                        className="google-logo"
                        onClick={() =>
                          window.open('https://translate.google.com/', '_blank')
                        }
                        glyph="google_color"
                        width="48px"
                        height="18px"
                        fill="none"
                        color="yellow"
                      />
                    ),
                  }}
                />
              </div>
            )}
        </div>
        {!isNil(overAllScore) && (
          <div
            className={`score-weight-wrap ${overAllScore?.scoring_group}`.trim()}
          >
            <label>{t('responses.card.score_weightage')}</label>
            <span className="score">{`${Number(
              overAllScore?.score_percent,
            )}%`}</span>
            <span className="sentiment">
              {t(`rating_group.${overAllScore?.scoring_group}`)}
            </span>
          </div>
        )}
      </div>
    </RCardExtended>
  );
};

export default ResponseCard;
